import { useEffect, useRef, useState } from 'react';
import useIncidentsService from '../services/incidentsService';
import { SEEN_EVENT, SEEN_KEY_PREFIX, getLastSeen } from '../utils/incidentsSeen';
import { detectedAt, isNewIncident } from '../pages/Incidents/incidentConstants';

export const BADGE_POLL_MS = 60_000;
/** Tras tantos fallos seguidos se deja de preguntar (ver abajo). */
export const MAX_CONSECUTIVE_FAILURES = 3;
/** Tope del API por página: más de esto se muestra como «99+» de todos modos. */
const PAGE_LIMIT = 100;

const isVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Cuántos incidentes NUEVOS hay para esta persona: los detectados después de lo último
 * que vio en la pantalla de incidentes. Es la «bola roja» del menú. Devuelve `null`
 * mientras no se sepa (no cargó, no corresponde o no hay acceso).
 *
 * Pregunta cada 60 s (la pantalla se refresca cada 15 s) y no con la pestaña del
 * navegador oculta. Se apaga a cero al instante cuando esa persona entra a la
 * pantalla de incidentes (aviso `SEEN_EVENT`), sin esperar al próximo ciclo.
 *
 * `onNew(incidents)` se llama cuando, entre un ciclo y el siguiente, aparecen incidentes
 * nuevos que antes no estaban. La primera lectura NO avisa: lo que ya había al entrar
 * es justo lo que muestra la bola roja. Sirve para la tarjeta y el sonido de aviso.
 *
 * Se calla en dos casos para no cargar al backend ni llenar la consola:
 *  - 403: administra la plataforma pero no está en `backoffice`; no tiene sentido
 *    volver a preguntar.
 *  - varios fallos seguidos (p. ej. ese ambiente aún no tiene la ruta). Se reintenta
 *    recién cuando cambie `enabled` o se vuelva a montar.
 */
export default function useNewIncidentsCount({
  enabled = true,
  userSub = '',
  pollMs = BADGE_POLL_MS,
  onNew,
} = {}) {
  const { listIncidents } = useIncidentsService();
  const [count, setCount] = useState(null);
  // Siempre el callback más reciente, sin reiniciar el sondeo cuando cambia.
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return undefined;
    }
    let cancelled = false;
    let stopped = false;
    let failures = 0;
    let timer;
    // Marca propia para avisar de llegadas, INDEPENDIENTE de «lo último visto»: la
    // pantalla de incidentes marca como visto lo que carga cada 15 s, y con eso el
    // sondeo de 60 s ya no vería como nuevo lo que acaba de llegar. Solo avanza.
    // `undefined` hasta la primera lectura; `null` = aún no había nada.
    let arrivalMark;

    const stop = () => {
      stopped = true;
      clearInterval(timer);
    };

    const load = async () => {
      if (cancelled || stopped || !isVisible()) return;
      const lastSeen = getLastSeen(userSub);
      // `since` filtra por última actualización: trae de más (incidentes viejos que
      // siguen fallando), y aquí se queda solo lo detectado después de lo visto.
      // Se pide desde lo más antiguo entre lo visto y la marca de llegadas: un incidente
      // detectado después de la marca se actualizó después de ella, así que viene seguro.
      const since = [lastSeen, arrivalMark].filter(Boolean).sort()[0];
      const result = await listIncidents({
        status: 'open',
        since: since || undefined,
        limit: PAGE_LIMIT,
      });
      if (cancelled) return;
      if (result.success) {
        failures = 0;
        const items = result.data.items;
        setCount(items.filter((item) => isNewIncident(item, lastSeen)).length);

        const detections = items.map(detectedAt).filter(Boolean);
        const newest = detections.length ? detections.reduce((a, b) => (a > b ? a : b)) : null;
        if (arrivalMark === undefined) {
          // Primera lectura: lo que ya había es la bola roja, no una llegada.
          arrivalMark = [lastSeen, newest].filter(Boolean).sort().pop() || null;
          return;
        }
        const arrived = items.filter((item) => isNewIncident(item, arrivalMark));
        if (arrived.length) {
          arrivalMark = [arrivalMark, newest].filter(Boolean).sort().pop();
          if (onNewRef.current) onNewRef.current(arrived);
        }
        return;
      }
      if (result.status === 403) {
        setCount(null);
        stop();
        return;
      }
      failures += 1;
      // Un fallo suelto conserva el último número; varios seguidos lo apagan.
      if (failures >= MAX_CONSECUTIVE_FAILURES) {
        setCount(null);
        stop();
      }
    };

    // Al volver al navegador se actualiza ya, sin esperar al próximo ciclo.
    const onVisibility = () => {
      if (isVisible()) load();
    };
    // Entró a la pantalla de incidentes (aquí o en otra pestaña): ya vio todo.
    const onSeen = (event) => {
      // `storage` llega por cualquier clave de localStorage: solo importa la nuestra.
      if (event.type === 'storage' && !String(event.key || '').startsWith(SEEN_KEY_PREFIX)) return;
      setCount((prev) => (prev === null ? prev : 0));
    };

    load();
    timer = setInterval(load, pollMs);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(SEEN_EVENT, onSeen);
    window.addEventListener('storage', onSeen);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(SEEN_EVENT, onSeen);
      window.removeEventListener('storage', onSeen);
    };
  }, [enabled, listIncidents, pollMs, userSub]);

  return count;
}
